// ============================================================
// FRONTEND: src/features/reports/components/ReportFilters.tsx
// ============================================================

import { Card, CardContent } from '@/components/ui/Card';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface ReportFiltersProps {
    visible: boolean;
    dateRange: { startDate: string; endDate: string };
    branchId: string;
    branches: any[];
    additionalFilters?: React.ReactNode;
    onDateRangeChange: (range: { startDate: string; endDate: string }) => void;
    onBranchChange: (branchId: string) => void;
    onClear: () => void;
    onQuickDate: (days: number) => void;
}

export function ReportFilters({
    visible, dateRange, branchId, branches, additionalFilters,
    onDateRangeChange, onBranchChange, onClear, onQuickDate
}: ReportFiltersProps) {
    if (!visible) return null;

    const hasFilters = dateRange.startDate || dateRange.endDate || branchId;

    return (
        <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
        >
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <button onClick={() => onQuickDate(0)} className="px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-slate-50">Today</button>
                        <button onClick={() => onQuickDate(7)} className="px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-slate-50">7 Days</button>
                        <button onClick={() => onQuickDate(30)} className="px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-slate-50">30 Days</button>
                        <button onClick={() => onQuickDate(90)} className="px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-slate-50">90 Days</button>
                        <button onClick={() => onQuickDate(365)} className="px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-slate-50">1 Year</button>

                        <div className="h-6 w-px bg-slate-200" />

                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={e => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
                            className="px-3 py-1.5 border rounded-lg text-xs"
                        />
                        <span className="text-xs text-slate-400">to</span>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={e => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
                            className="px-3 py-1.5 border rounded-lg text-xs"
                        />

                        {branches.length > 0 && (
                            <>
                                <div className="h-6 w-px bg-slate-200" />
                                <select
                                    value={branchId}
                                    onChange={e => onBranchChange(e.target.value)}
                                    className="px-3 py-1.5 border rounded-lg text-xs"
                                >
                                    <option value="">All Branches</option>
                                    {branches.map((b: any) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </>
                        )}

                        {additionalFilters}

                        {hasFilters && (
                            <button
                                onClick={onClear}
                                className="text-xs text-teal-600 font-medium flex items-center gap-1 ml-auto"
                            >
                                <X size={12} /> Clear All Filters
                            </button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}